import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBack } from '../lib/back.js';
import { theme } from '../theme.js';
import { useAuth } from '../context/AuthContext.js';
import { useSearch, useCategories } from '../lib/queries.js';
import type { SearchResult } from '../lib/queries.js';
import { PageShell } from '../components/PageShell.js';
import { Icon } from '../components/Icon.js';

export function SearchPage() {
  const back = useBack('/');
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';

  const [q, setQ] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [type, setType] = useState('');
  const [expirySoon, setExpirySoon] = useState(false);

  const { data: categories = [] } = useCategories(orgId);
  const { data: results, isFetching } = useSearch(orgId, {
    q: q.trim() || undefined,
    categoryId: categoryId || undefined,
    type: type || undefined,
    expirySoon: expirySoon || undefined,
  });

  const hasQuery = q.trim() || categoryId || type || expirySoon;

  return (
    <PageShell>
      {/* sticky header */}
      <div style={{
        padding: '14px 16px 12px',
        background: theme.surface,
        borderBottom: `1px solid ${theme.border}`,
        position: 'sticky', top: 0, zIndex: 5, flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <button onClick={back} aria-label="Back" style={iconBtn}><Icon name="back" size={18} /></button>
          <div style={{ fontSize: 22, fontWeight: 700, color: theme.text, letterSpacing: -0.5 }}>Search</div>
        </div>

        {/* search input */}
        <div style={{ position: 'relative' }}>
          <Icon name="search" size={16} style={{
            position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
            color: theme.textMuted, pointerEvents: 'none',
          }} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Equipment name or serial…"
            style={{
              width: '100%', padding: '10px 12px 10px 36px',
              background: theme.surface2, border: `1px solid ${theme.border}`,
              borderRadius: 10, fontSize: 14, color: theme.text,
              fontFamily: theme.font, outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>

        {/* filter chips row */}
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          {/* category filter */}
          <select
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            style={chipSelectStyle}
          >
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          {/* type filter */}
          <select value={type} onChange={(e) => setType(e.target.value)} style={chipSelectStyle}>
            <option value="">All types</option>
            <option value="tracked">Tracked</option>
            <option value="pooled">Pooled</option>
          </select>

          {/* expiry chip */}
          <button
            onClick={() => setExpirySoon(!expirySoon)}
            style={{
              padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600,
              border: `1px solid ${expirySoon ? theme.status.warn.dot : theme.border}`,
              background: expirySoon ? theme.status.warn.bg : theme.surface,
              color: expirySoon ? theme.status.warn.fg : theme.textDim,
              cursor: 'pointer', fontFamily: theme.font,
            }}
          >
            Expiring soon
          </button>
        </div>
      </div>

      {/* results */}
      <div style={{ flex: 1, overflowY: 'auto', background: theme.surface }}>
        {!hasQuery && (
          <div style={{ padding: '48px 24px', textAlign: 'center', color: theme.textMuted, fontSize: 14 }}>
            Search for equipment by name, category, or filter.
          </div>
        )}
        {hasQuery && isFetching && (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: theme.textMuted, fontSize: 14 }}>
            Searching…
          </div>
        )}
        {hasQuery && !isFetching && results?.length === 0 && (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: theme.textMuted, fontSize: 14 }}>
            No results
          </div>
        )}
        {results?.map((r, i) => (
          <ResultRow key={r.id} result={r} last={i === results.length - 1} />
        ))}
      </div>
    </PageShell>
  );
}

function ResultRow({ result, last }: { result: SearchResult; last: boolean }) {
  const expired = result.expiryDate && new Date(result.expiryDate) < new Date();
  const expiringSoon = !expired && result.expiryDate &&
    (new Date(result.expiryDate).getTime() - Date.now()) < 30 * 86400_000;

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      padding: '14px 18px',
      borderBottom: last ? 'none' : `1px solid ${theme.border}`,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 8, flexShrink: 0,
        background: theme.surface2, border: `1px solid ${theme.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: theme.textMuted,
      }}>
        <Icon name={result.equipmentType === 'tracked' ? 'package' : 'kit'} size={18} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: theme.text, lineHeight: 1.3 }}>
          {result.name}
        </div>
        <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2 }}>
          {[result.categoryName, result.serialNumber, result.assetName ?? result.locationName]
            .filter(Boolean).join(' · ')}
        </div>
        {(expired || expiringSoon) && (
          <span style={{
            display: 'inline-block', marginTop: 4,
            fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 999,
            background: expired ? theme.status.danger.bg : theme.status.warn.bg,
            color: expired ? theme.status.danger.fg : theme.status.warn.fg,
          }}>
            {expired ? 'EXPIRED' : 'EXPIRING SOON'}
          </span>
        )}
      </div>
    </div>
  );
}

const chipSelectStyle: React.CSSProperties = {
  padding: '6px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
  border: `1px solid ${theme.border}`, background: theme.surface,
  color: theme.textDim, cursor: 'pointer', fontFamily: theme.font,
  outline: 'none',
};

const iconBtn: React.CSSProperties = {
  width: 36, height: 36, border: `1px solid ${theme.border}`, background: theme.surface2, color: theme.text,
  borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0,
};
